import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { faqs } from '@/mocks/comics';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/da2lbd3d3r93mn1k3bbg';

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honey = (data.get('website_alt') || '').toString().trim();
    if (honey) {
      setFeedback({ type: 'success', message: '感谢您的反馈，我们会尽快处理。' });
      form.reset();
      return;
    }

    data.delete('website_alt');
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      if (res.ok && parsed?.code === 'OK') {
        setFeedback({ type: 'success', message: '感谢您的反馈，我们会尽快处理。' });
        form.reset();
      } else {
        setFeedback({ type: 'error', message: serverMsg || '提交失败，请稍后再试。' });
      }
    } catch {
      setFeedback({ type: 'error', message: '网络异常，请稍后再试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="常见问题 - 韩国漫画大全 · 每日更新 · 连载进度"
        description="韩国漫画大全常见问题：更新频率、本周更新汇总、连载进度同步、新作预告等疑问解答，并可在页面直接提交反馈。"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ]}
      />
      <Navbar />

      <main className="mx-auto max-w-4xl px-4 pb-16 pt-24 md:px-6 md:pb-24 md:pt-32">
        <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300"></i>
          <span className="text-foreground-800">常见问题</span>
        </nav>

        <div className="mb-8">
          <span className="text-xs font-medium tracking-wide text-accent-600">FAQ</span>
          <h1 className="mt-1 font-heading text-3xl text-foreground-950 md:text-4xl">常见问题</h1>
          <p className="mt-2 text-sm text-foreground-600">关于韩国漫画大全每日更新与连载进度的常见疑问，都在这里解答。</p>
        </div>

        {/* FAQ accordion */}
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const open = openIndex === i;
            return (
              <div
                key={f.q}
                className={`overflow-hidden rounded-lg border bg-background-50 transition-colors ${
                  open ? 'border-accent-300' : 'border-background-200'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(open ? null : i)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                  aria-expanded={open}
                >
                  <span className="flex items-start gap-3 text-sm font-semibold text-foreground-900 md:text-base">
                    <i className={`mt-0.5 ${open ? 'text-accent-500' : 'text-foreground-300'} ri-question-line`}></i>
                    {f.q}
                  </span>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800 transition-transform ${open ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </span>
                </button>
                {open && (
                  <div className="border-t border-background-100 px-5 py-4">
                    <p className="pl-7 text-sm leading-relaxed text-foreground-700">{f.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Contact form */}
        <section className="mt-14 rounded-lg bg-background-100/70 p-6 md:p-8">
          <h2 className="font-heading text-2xl text-foreground-950">没有找到答案？联系我们</h2>
          <p className="mt-2 text-sm text-foreground-600">填写下面的反馈表单，我们会尽快回复你的疑问。</p>

          {feedback && (
            <div
              className={`mt-4 flex items-center gap-2 rounded-lg px-4 py-3 text-sm ${
                feedback.type === 'success' ? 'bg-accent-100 text-accent-900' : 'bg-primary-100 text-primary-900'
              }`}
              role="status"
            >
              <i className={feedback.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
              {feedback.message}
            </div>
          )}

          <form onSubmit={onSubmit} data-readdy-form className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="f-name" className="text-sm font-medium text-foreground-800">称呼</label>
              <input
                id="f-name"
                name="name"
                type="text"
                required
                maxLength={30}
                placeholder="怎么称呼你"
                className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="f-email" className="text-sm font-medium text-foreground-800">邮箱</label>
              <input
                id="f-email"
                name="email"
                type="email"
                required
                maxLength={60}
                placeholder="用于接收回复"
                className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label htmlFor="f-topic" className="text-sm font-medium text-foreground-800">反馈主题</label>
              <select
                id="f-topic"
                name="topic"
                className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none focus:ring-2 focus:ring-primary-300"
              >
                <option value="内容建议">内容建议</option>
                <option value="追更问题">追更问题</option>
                <option value="合作咨询">合作咨询</option>
                <option value="其他">其他</option>
              </select>
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <label htmlFor="f-message" className="text-sm font-medium text-foreground-800">留言内容</label>
              <textarea
                id="f-message"
                name="message"
                rows={5}
                required
                maxLength={500}
                placeholder="请描述你的问题或建议（不超过 500 字）"
                className="resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-primary-300"
              ></textarea>
              <p className="text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            {/* honeypot */}
            <input
              name="website_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? '提交中…' : '提交反馈'}
              </button>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </>
  );
}